import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Write a Missionary Vision Letter (Case Statement) Before You Ask for Support';
const DESCRIPTION =
    "Before you send a single support letter, you need a vision letter — the foundational document that explains your calling, not just your budget. What it is, why it comes first, and how to write one people actually remember.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover12.jpg',
            datePublished: '2026-08-08',
            dateModified: '2026-08-08',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-write-a-missionary-vision-letter',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'What is a missionary vision letter?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "A vision letter (sometimes called a case statement) is the foundational document that explains your calling, your field, and why you're going — written before you ever ask anyone for a specific dollar amount. It's the story behind the ask, not the ask itself.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How is a vision letter different from a support letter?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "A support letter makes a specific, time-bound request for a dollar amount and usually goes to one person at a time. A vision letter is broader and comes first — it explains who you are and why you're going, and it's the document you point back to when a specific ask follows.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How long should a missionary vision letter be?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Long enough to tell the real story, short enough that someone reads the whole thing. One page, front and back at most, is the standard target — most first drafts run too long and need a second pass focused on cutting, not adding.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Do I need a vision letter if I already have a support-raising page?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes — a page shows the ask, but most people still decide whether they care about your calling from a personal letter or conversation first. The vision letter is what gets someone to click through to the page in the first place, not a replacement for it.",
                    },
                },
            ],
        },
    ],
};

export default function HowToWriteAMissionaryVisionLetter() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-write-a-missionary-vision-letter' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-vision-letter-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover12.jpg" alt="A missionary writing at a desk, drafting the story behind their calling" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Most first-time support raisers skip straight to the ask: a letter with a dollar figure, a giving link, and a deadline. It usually falls flat, not because the need isn't real, but because the reader doesn't yet know why it matters to them. The document that fixes this comes first, not alongside the ask — a vision letter.</p>

                    <h2>What a vision letter actually is</h2>

                    <p>A vision letter (some call it a case statement) explains your calling, your field, and why you're going — before it asks anyone for anything specific. It's the story a reader needs in order to care about the ask that follows, whether that ask comes in the same letter, a follow-up conversation, or a link to your <a href="/how-it-works">support page</a>. Skip it, and every future ask has to work twice as hard, because the reader is deciding whether to trust you and whether to give in the same breath.</p>

                    <h2>Why it has to come before the ask</h2>

                    <p>People rarely give to a need in the abstract. They give to a person and a story they believe in. A vision letter does the work of building that belief first, so that when a specific request arrives — a monthly amount, a launch date, a goal — the reader isn't evaluating a stranger's budget, they're responding to someone whose calling they already understand. See <a href="/resources/is-it-biblical-to-ask-for-support">is it biblical to ask for support</a> for the theology behind why this two-step approach isn't a marketing trick — it mirrors how support raising has worked throughout church history.</p>

                    <h2>What to include</h2>

                    <p>A strong vision letter covers four things, in roughly this order:</p>
                    <ul>
                        <li><strong>Your story.</strong> How you came to this calling — briefly, specifically, and honestly. Skip the generic "God laid it on my heart" language in favor of the actual moment or process.</li>
                        <li><strong>The need on the field.</strong> What's happening where you're going, and why it matters — grounded in specifics (a place, a people group, a gap) rather than vague global statistics.</li>
                        <li><strong>Your role.</strong> What you'll actually be doing day to day. Vague titles ("missionary," "church planter") mean less to most readers than a concrete description of the work.</li>
                        <li><strong>What happens next.</strong> A short, honest note that support raising is part of this calling, without yet naming a number — that's what the follow-up ask is for.</li>
                    </ul>

                    <h2>Keep it to one page</h2>

                    <p>Long enough to tell the real story, short enough that someone actually reads the whole thing — one page, front and back at most, is the standard target. Almost every first draft runs long. The second pass should be about cutting, not adding: remove the sentence that restates something you already said, the paragraph that explains context the reader doesn't need yet, the qualifier that hedges a claim you could just make directly.</p>

                    <h2>How it connects to your support letter</h2>

                    <p>The vision letter and the <a href="/resources/how-to-write-a-missionary-support-letter">support letter</a> aren't competing documents — they're sequential. The vision letter goes out first, sometimes months ahead, to build understanding and interest. The support letter follows with the specific, time-bound ask: a dollar amount, a way to give, a deadline. Sending the ask without the groundwork is the single most common reason a first support letter underperforms.</p>

                    <h2>Point it at something concrete</h2>

                    <p>A vision letter that ends with "I'll be in touch" leaves the reader with nothing to do right now. Give it a next step — even a soft one, like "you can follow along at [your page]" — so someone who's moved by the story has somewhere to go immediately instead of waiting for a follow-up that might arrive weeks later. <a href="/">Set up your Devotion Fund page</a> before you send your first vision letter, so that next step already exists.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>What is a missionary vision letter?</strong><br />
                    The foundational document that explains your calling and your field before you ask anyone for a specific dollar amount — the story behind the ask, not the ask itself.</p>

                    <p><strong>How is a vision letter different from a support letter?</strong><br />
                    A support letter makes a specific, time-bound request. A vision letter comes first and explains who you are and why you're going.</p>

                    <p><strong>How long should a missionary vision letter be?</strong><br />
                    One page, front and back at most — long enough to tell the real story, short enough to actually get read.</p>

                    <p><strong>Do I need a vision letter if I already have a support-raising page?</strong><br />
                    Yes — the letter is usually what gets someone to click through to the page in the first place, not a replacement for it.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter</a></li>
                        <li><a href="/resources/is-it-biblical-to-ask-for-support">Is It Biblical to Ask for Support?</a></li>
                        <li><a href="/resources/raise-support-fast-30-60-90-day-plan">Raise Support Fast: A 30-60-90 Day Plan</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built this platform after seeing how many missionaries jumped straight to the ask and wondered why it didn't land — the vision letter is the step that's easiest to skip and the hardest one to skip well.</p>
                    <p><small><em>Last updated: 2026-08-08.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-write-a-missionary-vision-letter" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
